/**
 * Local landing page for “bakery in Barrie” / “bakeries in Barrie”.
 * Real NAP, hours, map, and FAQs — not a thin keyword doorway.
 */
import { Link } from 'react-router-dom';
import { BakerySchema, BreadcrumbSchema, FaqSchema } from '../components/StructuredData';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';
import { BAKERY_LOCAL, BAKERY_LOCAL_FAQS } from '../utils/bakeryLocal';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';

export default function BakeryInBarrie() {
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.bakeryInBarrie,
    og: {
      type: 'website',
      title: PAGE_SEO.bakeryInBarrie.title,
      description: PAGE_SEO.bakeryInBarrie.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });

  return (
    <div>
      <BakerySchema />
      <FaqSchema />
      <BreadcrumbSchema
        items={[
          { name: "Nanny B's Bakery", url: 'https://nannybbakery.ca/' },
          { name: 'Bakery in Barrie', url: 'https://nannybbakery.ca/bakery-in-barrie' },
        ]}
      />

      <section className="gradient-primary text-white section-padding">
        <div className="container-custom max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
            Bakery in Barrie — custom cakes and fresh treats
          </h1>
          <p className="text-lg text-gray-100">
            Nanny B&apos;s Bakery is a family bakery in south Barrie. We bake custom cakes,
            stuffed cookies, cheesecakes, butter tarts, and daily treats for pickup at
            Commerce Park Drive.
          </p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">Visit us in Barrie</h2>
            <p className="text-gray-700 mb-2">{BAKERY_LOCAL.addressFull}</p>
            <p className="text-gray-700 mb-2">
              Phone:{' '}
              <a className="text-primary-600 underline" href="tel:17055034966">
                {BAKERY_LOCAL.telephone}
              </a>
            </p>
            <p className="text-gray-700 mb-4">{BAKERY_LOCAL.hoursShort}</p>
            <p className="text-gray-700 mb-6">
              We are in south Barrie on Commerce Park Drive, same street as Galaxy Cinemas Barrie
              — not downtown.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/menu" className="btn-primary">
                View the menu
              </Link>
              <Link to="/order" className="btn-secondary">
                Order for pickup
              </Link>
              <a
                href={BAKERY_LOCAL.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                Open in Google Maps
              </a>
            </div>
          </div>
          <iframe
            title="Map of Nanny B's Bakery in Barrie"
            src={BAKERY_LOCAL.mapsEmbedUrl}
            className="w-full min-h-[16rem] rounded-lg border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom max-w-3xl">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">What we bake</h2>
          <p className="text-gray-700 mb-4">
            Custom cakes (72 hour notice), cheesecakes, stuffed cookies, cookies, tarts, bars,
            pastries, and sandwiches. Website orders are pickup here; delivery is through Uber Eats.
          </p>
          <Link to="/gallery" className="text-primary-600 underline">
            See real cake photos
          </Link>
        </div>
      </section>

      <section id="common-questions" className="section-padding bg-white">
        <div className="container-custom max-w-3xl">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
            Common questions about our Barrie bakery
          </h2>
          <dl className="space-y-6">
            {BAKERY_LOCAL_FAQS.map((item) => (
              <div key={item.question}>
                <dt className="font-semibold text-gray-900">{item.question}</dt>
                <dd className="text-gray-700 mt-1">{item.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
}
